import { forwardRef, useRef, useCallback, useState, ChangeEvent, FocusEvent, FocusEventHandler, ReactNode } from "react";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { Icon } from "capitalrx-components-2.0/lib/general";
import classNames from "classnames";
import { InputChangeHandler } from "../InputChangeHandler";
import { useValidity } from "../ValidityGroup";
import { useSubmitting } from "../SubmitGroup";
import styles from "./styles.module.scss";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `Check` component properties.
 */
export interface CheckProps extends Pick<AccessibilityProps, "aria-label"> {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * Indicates whether the input is disabled.
   */
  readonly disabled?: boolean;

  /**
   * Indicates whether the input is read-only.
   */
  readonly readOnly?: boolean;

  /**
   * The tab index.
   */
  readonly tabIndex?: number;

  /**
   * Indicates whether to focus the input on initialization.
   */
  readonly autoFocus?: boolean;

  /**
   * The value.
   */
  readonly value: boolean | null | "indeterminate";

  /**
   * The default value.
   */
  readonly defaultValue: boolean | null | "indeterminate";

  /**
   * Instructions displayed after the input.
   */
  readonly instructions?: ReactNode;

  /**
   * The function to call when the value is to be changed.
   */
  readonly onChange?: InputChangeHandler<boolean | null | "indeterminate">;

  /**
   * The function to call when the input is focused.
   */
  readonly onFocus?: FocusEventHandler;

  /**
   * The function to call when the input loses focus.
   */
  readonly onBlur?: FocusEventHandler;
}

/**
 * The check component.
 */
export const Check = forwardRef<HTMLInputElement, CheckProps>(function Check(props, ref) {
  const {
    id,
    required = false,
    disabled = false,
    readOnly = false,
    tabIndex,
    autoFocus,
    defaultValue = null,
    instructions,
    "aria-label": ariaLabel,
    onChange,
    onFocus,
    onBlur,
  } = props;

  const { value = defaultValue } = props;

  const checked = value === true;
  const indeterminate = value === "indeterminate";

  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const checkContainerRef = useRef<HTMLSpanElement | null>(null);
  const [hover, setHover] = useState(false);
  const labelMouseEnterHandler = useCallback((ev: Event) => {
    setHover(true);
    ev.stopPropagation();
  }, []);

  const labelMouseExitHandler = useCallback((ev: Event) => {
    setHover(false);
    ev.stopPropagation();
  }, []);

  const checkContainerRefHandler = useCallback((ref: HTMLSpanElement | null) => {
    if (checkContainerRef.current) {
      checkContainerRef.current.removeEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      checkContainerRef.current.removeEventListener("cust_labelMouseExit", labelMouseExitHandler);
    }

    if (ref) {
      ref.addEventListener("cust_labelMouseEnter", labelMouseEnterHandler);
      ref.addEventListener("cust_labelMouseExit", labelMouseExitHandler);
    } else {
      setHover(false);
    }

    checkContainerRef.current = ref;
  }, []);

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (readOnly || submitting) return;

      onChange?.(ev.target.checked, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const blurHandler = useCallback(
    (ev: FocusEvent) => {
      const { currentTarget, relatedTarget } = ev;

      /*
      If for some reason our focus is moving to a different element (like, let's say, the label), don't raise the focus
      exit event, because we're actually going to retain the focus on the element.
    */
      if (relatedTarget && currentTarget.parentElement && isDescendentOf(currentTarget.parentElement, relatedTarget)) {
        ev.preventDefault();
        return;
      }

      onBlur?.(ev);
    },
    [onBlur],
  );

  return (
    <div
      ref={checkContainerRefHandler}
      className={classNames(styles.Check, "check", {
        checked,
        indeterminate,
        disabled,
        invalid,
        readonly: readOnly || submitting,
        hover,
      })}
      >
      <input
        id={id}
        className="input"
        ref={ref}
        type="checkbox"
        aria-label={ariaLabel ?? undefined}
        aria-describedby={instructions ? `${id}-instructions` : undefined}
        aria-labelledby={`${id}-label`}
        aria-checked={indeterminate ? "mixed" : checked}
        tabIndex={readOnly || disabled || submitting ? -1 : tabIndex ?? 0}
        aria-disabled={disabled || readOnly || submitting}
        aria-required={required}
        aria-invalid={invalid}
        checked={checked}
        disabled={disabled}
        autoFocus={autoFocus}
        readOnly={readOnly || submitting}
        onFocus={onFocus}
        onChange={changeHandler}
        onBlur={blurHandler}
      />
      <div aria-hidden="true" className="check-container">
        {checked && <Icon name="check-mark" size="small"/>}
        {indeterminate && <Icon name="remove" />}
      </div>
    </div>
  );
});
